import { defineStore } from 'pinia';
import { useTrialFundStore } from '@/stores/trialFund';
import { getUserId } from '@/utils/auth-token';

const DONE_PREFIX = 'ds-jackpot-guide-done:';

/** @typedef {'join' | 'pick' | 'pay' | ''} JackpotGuideStep */

function doneStorageKey() {
  const uid = String(getUserId() || '').trim() || 'guest';
  return `${DONE_PREFIX}${uid}`;
}

function readDone() {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(doneStorageKey()) === '1';
  } catch {
    return false;
  }
}

function writeDone() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(doneStorageKey(), '1');
  } catch {
    /* ignore quota */
  }
}

export const useJackpotGuideStore = defineStore('jackpotGuide', {
  state: () => ({
    open: false,
    /** @type {JackpotGuideStep} */
    step: '',
    /** pick 步已选号，等点底部「立即参与」 */
    pickReady: false,
  }),
  getters: {
    active: (state) => state.open && Boolean(state.step),
  },
  actions: {
    /**
     * 首次自动开引导。已完成、福利盘引导进行中、或已经打开时不抢。
     * @param {'join' | 'pick'} step
     */
    tryStart(step) {
      if (this.open) return false;
      if (readDone()) return false;
      if (useTrialFundStore().guideActive) return false;
      this.open = true;
      this.step = step;
      this.pickReady = false;
      return true;
    },

    advanceFromJoin() {
      if (!this.open || this.step !== 'join') return;
      this.step = 'pick';
      this.pickReady = false;
    },

    /** 选号完成，手指改指底部「立即参与」 */
    markPickReady() {
      if (!this.open || this.step !== 'pick' || this.pickReady) return;
      this.pickReady = true;
    },

    /** 选号被清空，手指回到号码 */
    resetPickPoint() {
      if (!this.open || this.step !== 'pick' || !this.pickReady) return;
      this.pickReady = false;
    },

    advanceFromPick() {
      if (!this.open || this.step !== 'pick') return;
      this.step = 'pay';
      this.pickReady = false;
    },

    backToPick() {
      if (!this.open || this.step !== 'pay') return;
      this.step = 'pick';
      this.pickReady = false;
    },

    rewindToJoin() {
      if (!this.open) return;
      if (this.step !== 'pick' && this.step !== 'pay') return;
      this.step = 'join';
      this.pickReady = false;
    },

    finish() {
      if (!this.open) return;
      writeDone();
      this.open = false;
      this.step = '';
      this.pickReady = false;
    },

    closeWithoutDone() {
      this.open = false;
      this.step = '';
      this.pickReady = false;
    },
  },
});
